import { ComputedRef, inject, reactive, Ref, unref } from 'vue';

import { toContentModel } from '@/components/community/utils/converter';
import { ContentModel } from '@/components/community/utils/types';
import {
  CommunityComment,
  CommunityPost
} from '@/shared/composables/community';
import { CommunityAuthor } from '@/shared/composables/community/profile';
import { useTranslations } from '@/shared/composables/useTranslations';

export const useCommunityAuth = () => {
  const profile =
    inject<ComputedRef<CommunityAuthor | null | undefined>>('profile');
  const initLogin = inject<() => void>('initLogin');

  if (!profile) {
    throw new Error('Community: "profile" not provided.');
  }

  if (!initLogin) {
    throw new Error('Community: "initLogin" not provided.');
  }

  return {
    profile,
    initLogin
  };
};

// TODO: consider making `maxMessageLength` mandatory,
// once this composable's contentModel validation is broaaly adopted.
export type ContentModelOptions = {
  maxMessageLength?: number;
  minImages?: number;
  maxImages?: number;
};

type ContentModelErrors = { body?: string; images?: string };

export const useContentModel = (
  content:
    | CommunityPost
    | CommunityComment
    | Ref<CommunityPost | CommunityComment>
    | null = null,
  options: ContentModelOptions = {}
) => {
  const { t } = useTranslations();

  const contentModel = reactive<ContentModel>(
    content
      ? toContentModel(unref(content))
      : {
          __type: 'new',
          body: '',
          images: []
        }
  );

  const errors = reactive<ContentModelErrors>({});

  const reset = () => {
    contentModel.body = '';
    contentModel.images = [];
  };

  const validateBody = () => {
    delete errors.body;

    if (
      options.maxMessageLength &&
      contentModel.body.length > options.maxMessageLength
    ) {
      errors.body = t('community.shared.errors.text_length_limit_exceeded', {
        above: contentModel.body.length - options.maxMessageLength
      });
    }

    return !errors.body;
  };

  const validateImages = () => {
    delete errors.images;

    if (options.minImages && contentModel.images.length < options.minImages) {
      errors.images = t(
        'community.shared.errors.min_images_not_reached',
        options.minImages
      );
    } else if (
      options.maxImages &&
      contentModel.images.length > options.maxImages
    ) {
      errors.images = t('community.shared.errors.text_length_limit_exceeded', {
        above: contentModel.images.length - options.maxImages
      });
    }

    return !errors.images;
  };

  const validateAll = () => {
    const validBody = validateBody();
    const validImages = validateImages();

    return validBody && validImages;
  };

  return {
    contentModel,
    errors,
    reset,
    validateAll,
    validateBody,
    validateImages
  };
};
